import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Filter, Search } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { AppNavbar } from "@/components/layout/AppNavbar";

interface Standard {
  id: string;
  title: string;
  category: string;
  status: string;
  version?: string;
  scope?: string;
  purpose?: string;
  identifier?: string;
  created_at: string;
}

const Standards = () => {
  const [standards, setStandards] = useState<Standard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchStandards();
  }, []);
  
  const fetchStandards = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('standards')
        .select('*');
      
      if (error) {
        throw error;
      }
      
      if (data) {
        setStandards(data);
      }
    } catch (error: any) {
      console.error("Error fetching standards:", error);
      toast.error(error.message || "Error loading standards");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      toast.success("You have been logged out");
      navigate("/login");
    } catch (error: any) {
      console.error("Logout error:", error);
      toast.error(error.message || "Logout failed");
    }
  };

  // Get unique categories for filtering
  const categories = Array.from(new Set(standards.map(std => std.category)));
  
  const filteredStandards = standards.filter(standard => 
    (standard.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
     standard.category.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!categoryFilter || standard.category === categoryFilter)
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AppNavbar />

      <main className="flex-1 container py-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Standards Library</h1>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <div className="relative flex-1 w-full sm:max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search standards..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                className="pl-10 pr-4 py-2 w-full rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                value={categoryFilter || ''}
                onChange={(e) => setCategoryFilter(e.target.value || null)}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        </div>
        
        <Card className="shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="mt-2 text-muted-foreground">Loading standards...</p>
                  </TableCell>
                </TableRow>
              ) : filteredStandards.length > 0 ? (
                filteredStandards.map((standard) => (
                  <TableRow 
                    key={standard.id} 
                    className="hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/standards/${standard.id}`)}
                  >
                    <TableCell className="font-medium">
                      {standard.title}
                      {standard.identifier && <span className="text-xs text-muted-foreground ml-2">({standard.identifier})</span>}
                    </TableCell>
                    <TableCell>{standard.category}</TableCell>
                    <TableCell>{standard.version || 'N/A'}</TableCell>
                    <TableCell>
                      <span 
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          standard.status === 'Active' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                        }`}
                      >
                        {standard.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/standards/${standard.id}`);
                        }}
                      >
                        View Details
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                    No standards found. Try adjusting your search or filter criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </main>
    </div>
  );
};

export default Standards;
